import { MarketName, CryptoExchange } from '@/constants';
import tradingAPI from '@/features/api/chart/trading';
import * as Sentry from '@sentry/nextjs';
import { useMutation } from '@tanstack/react-query';

// MEMO: https://timepercentworkspace.slack.com/archives/C04GPV5H6R1/p1744089054019809?thread_ts=1744079431.005529&cid=C04GPV5H6R1
interface TradingError {
  response: {
    data: {
      error_code: 200 | 201 | 202 | 203 | 204 | 205 | 206;
      error_message: string;
    };
  };
}

const messageByErrorCode = {
  200: '로봇 작동을 위해 먼저 코인원 계정 연결이 필요해요.',
  201: '계좌 정보를 불러오는 데 문제가 있어요. 코인원 앱에서 계정 상태를 확인해 주세요.',
  202: '로봇은 동시에 최대 5개까지만 작동할 수 있어요. 기존 로봇을 삭제하고 다시 시도해 주세요.',
  203: '같은 전략은 동시에 실행할 수 없어요. 다른 전략을 선택해 주세요.',
  204: '코인원 계좌 잔액이 부족해요. 잔액을 확인해 주세요.',
  205: '로봇 작동 시작에 실패했어요. 다시 시도해 주세요.',
  206: '로봇 작동 시작에 실패했어요. 다시 시도해 주세요.',
} as const;

const isTradingError = (error: unknown): error is TradingError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'data' in error.response &&
    typeof error.response.data === 'object' &&
    error.response.data !== null &&
    'error_code' in error.response.data &&
    typeof error.response.data.error_code === 'number'
  );
};

interface CreateTradingVariables {
  name: string;
  exchange: CryptoExchange;
  market: MarketName;
  principal: number;
  strategy_id: string;
}

type StartTradingV2Response = Awaited<
  ReturnType<typeof tradingAPI.startTradingV2>
>;

const useStartTradingV2Mutation = () => {
  return useMutation<
    StartTradingV2Response,
    TradingError,
    CreateTradingVariables
  >({
    mutationFn: async ({ name, exchange, market, principal, strategy_id }) => {
      try {
        const response = await tradingAPI.startTradingV2({
          name,
          exchange,
          market,
          principal,
          strategy_id,
        });
        Sentry.captureMessage('Start Trading 요청', {
          level: 'info',
          extra: {
            name,
            exchange,
            market,
            principal,
            strategy_id,
            response,
          },
        });

        return response;
      } catch (error) {
        if (!isTradingError(error)) {
          Sentry.captureMessage('Start Trading 요청 실패 - 알 수 없는 오류', {
            level: 'error',
            extra: {
              error,
            },
          });

          return Promise.reject(
            '알 수 없는 오류로 로봇 작동 시작에 실패했어요. 계속해서 로봇 작동에서 실패한다면 채널톡을 통해 문의해 주세요.',
          );
        }

        const errorMessage = messageByErrorCode[error.response.data.error_code];
        Sentry.captureMessage('Start Trading 요청 실패 - 트레이딩 오류', {
          level: 'error',
          extra: {
            error,
            errorMessage,
            errorCode: error.response.data.error_code,
            name,
            exchange,
            market,
            principal,
            strategy_id,
          },
        });
        return Promise.reject(errorMessage);
      }
    },
  });
};

export default {
  useStartTradingV2Mutation,
};
